import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Form, Image, Row, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { CustomForm, CustomModal } from "../../components";
import {
  useCreateNewsMutation,
  useDeleteNewsMutation,
  useFetchAllNewsQuery,
  useFilterNewsByUserQuery,
  useUpdateNewsMutation,
} from "../../store/api/apiSlice";
import { Button as CustomBtn } from "../../ui";

const baseUrl = process.env.REACT_APP_SERVER_URL;
const secretKey = process.env.REACT_APP_SECRET_KEY;

const NewsManagement = () => {
  const { data: newsData = { news: [] }, refetch } = useFetchAllNewsQuery();

  const [createNews] = useCreateNewsMutation();
  const [updateNews] = useUpdateNewsMutation();
  const [deleteNews] = useDeleteNewsMutation();
  const [newsType, setNewsType] = useState();
  const [saveButton, setSaveButton] = useState(false);
  const [filters, setFiltersButton] = useState(false);

  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isViewModalOpen, setViewModalOpen] = useState(false);
  const [currentNewsId, setCurrentNewsId] = useState(null);
  const [viewedNews, setViewedNews] = useState(null);
  const [filterParams, setFilterParams] = useState(null);
  const [image, setImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [schools, setSchools] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [tags, setTags] = useState([]);
  const [favSports, setFavSports] = useState([]);

  const [selectedSports, setSelectedSports] = useState([]);

  const titleRef = useRef(null);
  const categoryRef = useRef(null);
  const sourceRef = useRef(null);
  const tagsRef = useRef(null);
  const detailsRef = useRef(null);
  const urlRef = useRef(null);
  const schoolMascotRef = useRef(null);
  const schoolNameRef = useRef(null);
  const genderRef = useRef(null);
  const userIdRef = useRef(null);
  const sportsRef = useRef(null);
  const schoolsRef = useRef(null);

  const genderOptions = ["male", "female", "other"];

  useEffect(() => {
    const fetchSchools = async () => {
      const xAuthToken =
        JSON.parse(localStorage.getItem("token") || "{}")?.token || "";
      const headers = {
        "secret-key": secretKey,
        "x-auth-token": xAuthToken,
      };

      try {
        const response = await fetch(`${baseUrl}/api/university/get-all`, {
          method: "GET",
          headers,
        });
        if (response.ok) {
          const data = await response.json();
          setSchools(data.university || []);
        } else {
          toast.error("Failed to fetch schools");
        }
      } catch (error) {
        toast.error("An error occurred while fetching schools");
      }
    };

    fetchSchools();
  }, []);

  useEffect(() => {
    if (isEditing && isModalOpen && currentNewsId) {
      const newsItem = newsData.news.find((item) => item._id === currentNewsId);
      if (newsItem) {
        titleRef.current.value = newsItem.title;
        categoryRef.current.value = newsItem.category;
        sourceRef.current.value = newsItem.source;

        detailsRef.current.value = newsItem.details;
        urlRef.current.value = newsItem.url;
        schoolMascotRef.current.value = newsItem.schoolMascot || "";
        schoolNameRef.current.value = newsItem.schoolName || "";
        genderRef.current.value = newsItem.gender || "";
        setImage(`${baseUrl}/${newsItem.image}`);
        setImageFile(null);
        let parsedVoteTitles = [];
        try {
          parsedVoteTitles = JSON.parse(newsItem.tags[0]);

          parsedVoteTitles = parsedVoteTitles.map((tag) => tag.trim());
        } catch (error) {
          parsedVoteTitles = [];
        }

        setTags(parsedVoteTitles);
      }
    }
  }, [isModalOpen, isEditing, currentNewsId, newsData]);

  const openModal = () => {
    setSaveButton(false);

    setIsEditing(false);
    setModalOpen(true);
  };

  const closeModal = () => {
    setSaveButton(false);
    setModalOpen(false);
    setIsEditing(false);
    setImage(null);
    setImageFile(null);
    setCurrentNewsId(null);
  };

  const openViewModal = async (id) => {
    const xAuthToken = JSON.parse(localStorage.getItem("token"))?.token || "";
    try {
      const response = await fetch(`${baseUrl}/api/news/get-single/${id}`, {
        headers: { "secret-key": secretKey, "x-auth-token": xAuthToken },
      });
      if (response.ok) {
        const data = await response.json();
        setViewedNews(data.news);

        setViewModalOpen(true);
      } else {
        toast.error("Failed to fetch news details.");
      }
    } catch (error) {
      toast.error("An error occurred while fetching news details.");
    }
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setViewedNews(null);
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImage(reader.result);
      reader.readAsDataURL(file);
      setImageFile(file);
    }
  };
  const handleSelect = (sport) => {
    if (!selectedSports.includes(sport)) {
      setSelectedSports([...selectedSports, sport]);
    }
    setDropdownOpen(false);
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };
  const handleSubmit = async (e) => {
    setSaveButton(true);

    e.preventDefault();
    const formData = new FormData();
    formData.append("title", titleRef.current.value);
    formData.append("category", categoryRef.current.value);
    formData.append("source", sourceRef.current.value);
    const formattedTags = tags.map((tag) => tag.trim());
    formData.append("tags", JSON.stringify(formattedTags));
    formData.append("details", detailsRef.current.value);
    formData.append("url", urlRef.current.value);
    formData.append("schoolName", schoolNameRef.current.value);
    formData.append("schoolMascot", schoolMascotRef.current.value);
    formData.append("gender", genderRef.current.value);
    formData.append("newsType", newsType);
    formData.append("sports_tags", JSON.stringify(selectedSports));
    if (imageFile) formData.append("image", imageFile);

    try {
      if (isEditing) {
        await updateNews({ id: currentNewsId, newsData: formData }).unwrap();
        setSaveButton(false);

        toast.success("News updated successfully!");
      } else {
        await createNews(formData).unwrap();
        setSaveButton(false);

        toast.success("News created successfully!");
      }
      refetch();
      closeModal();
      setSelectedSports([]);
    } catch (error) {
      setSaveButton(false);

      toast.error("Error saving news");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteNews(id).unwrap();
      toast.success("News deleted successfully");
      refetch();
    } catch (error) {
      toast.error("Error deleting news");
    }
  };

  const handleFilter = (e) => {
    setFiltersButton(true);
    e.preventDefault();
    const filterData = {
      title: userIdRef.current.value ? userIdRef.current.value : "",
      sports: sportsRef.current.value ? sportsRef.current.value.split(",") : [],
      schools: schoolsRef.current.value
        ? schoolsRef.current.value.split(",")
        : [],
      gender: genderRef.current.value,
    };

    setFiltersButton(false);
    setFilterParams(filterData);
  };

  const handleEdit = (news) => {
    setIsEditing(true);
    setCurrentNewsId(news._id);
    const sportsTags = JSON.parse(news.sports_tags[0]);
    setFavSports(sportsTags);
    setSelectedSports(sportsTags);
    setModalOpen(true);
    setModalOpen(true);
  };

  const filteredNewsData = newsData.news.filter((news) => {
    const matchesTitle = news?.title
      ?.toLowerCase()
      .includes(filterParams?.title?.toLowerCase() || "");

    const matchesSports =
      filterParams?.sports.length === 0 ||
      filterParams?.sports.some((sport) =>
        news.sports_tags?.[0]
          ?.toLowerCase()
          .includes(sport.trim().toLowerCase())
      );

    const matchesSchools =
      filterParams?.schools.length === 0 ||
      filterParams?.schools.some((school) =>
        news.schoolName
          ?.replace(/"/g, "")
          .trim()
          .toLowerCase()
          .includes(school.replace(/"/g, "").trim().toLowerCase())
      );

    const matchesGender =
      !filterParams?.gender ||
      (news.gender &&
        news.gender.toLowerCase() === filterParams.gender.toLowerCase());

    return matchesTitle && matchesSports && matchesSchools && matchesGender;
  });

  const displayedNews = filterParams ? filteredNewsData : newsData.news;

  const safeDisplayedNews = displayedNews.map((news) => {
    if (news.tags && news.tags.length > 0) {
      try {
        const parsedTags = JSON.parse(news.tags[0]);

        return {
          ...news,
          tags: parsedTags.join(","),
        };
      } catch (error) {
        return {
          ...news,
          tags: news.tags[0],
        };
      }
    }

    return news;
  });
  const sortedNews = safeDisplayedNews.sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );
  const paginatedUsers = sortedNews.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(sortedNews.length / itemsPerPage);

  const getPaginationRange = () => {
    const range = [];
    const maxButtons = 10;
    const startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    const endPage = Math.min(totalPages, startPage + maxButtons - 1);

    for (let i = startPage; i <= endPage; i++) {
      range.push(i);
    }

    return range;
  };

  const paginationRange = getPaginationRange();
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      addTag(inputValue.trim());
      setInputValue("");
    }
  };

  const addTag = (tag) => {
    if (!tags.includes(tag)) {
      setTags((prevTags) => [...prevTags, tag]);
    }
  };

  const removeTag = (tagToRemove) => {
    setTags((prevTags) => prevTags.filter((tag) => tag !== tagToRemove));
  };
  useEffect(() => {
    const fetchFavSports = async () => {
      const xAuthToken = JSON.parse(localStorage.getItem("token"))?.token;

      try {
        const response = await fetch(`${baseUrl}/api/sports/get-all`, {
          method: "GET",
          headers: {
            "secret-key": secretKey,
            "x-auth-token": xAuthToken,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setFavSports(data.sports || []);
        } else {
        }
      } catch (error) {}
    };

    fetchFavSports();
  }, []);
  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "22px",
        }}
      >
        <h3>News Management</h3>
        <Button onClick={openModal}>Create News</Button>
      </div>
      <Form onSubmit={handleFilter} className="mt-4 mb-4">
        <Row>
          <Col>
            <Form.Control placeholder="Title" ref={userIdRef} />
          </Col>
          <Col>
            <Form.Control placeholder="Sports" ref={sportsRef} />
          </Col>
          <Col>
            <Form.Control placeholder="Schools" ref={schoolsRef} />
          </Col>
          <Col>
            <Form.Control placeholder="Gender" ref={genderRef} />
          </Col>
          <Col>
            <Button type="submit">Apply Filter</Button>
          </Col>
        </Row>
      </Form>

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Title</th>
            <th>Category</th>
            <th>Source</th>
            <th>School Name</th>
            {/* <th>School Mascot</th> */}
            <th>Gender</th>
            <th>Sports Tags</th>
            {/* <th>Tags</th> */}

            {/* <th>Image</th> */}
            <th>Created Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedUsers && paginatedUsers.length > 0 ? (
            paginatedUsers.map((news) => (
              <tr
                key={news._id}
                onClick={() => openViewModal(news._id)}
                style={{ cursor: "pointer" }}
              >
                <td>{news.title}</td>
                <td>{news.category}</td>
                <td>{news.source}</td>
                <td>{news.schoolName}</td>
                {/* <td>{news.schoolMascot}</td> */}
                <td>{news.gender}</td>
                <td>
                  {news?.sports_tags && news?.sports_tags.length > 0
                    ? (() => {
                        try {
                          const parsedSport = JSON.parse(news?.sports_tags[0]);
                          return parsedSport.join(",");
                        } catch (error) {
                          return news?.sports_tags[0];
                        }
                      })()
                    : "No Sports available"}
                </td>
                {/* <td>{news.tags}</td> */}

                {/* <td>
                  {news.image && (
                    <Image
                      src={`${baseUrl}/${news.image}`}
                      alt="news"
                      width="50"
                      height="50"
                      thumbnail
                    />
                  )}
                </td> */}
                <td>{news.createdAt}</td>
                <td>
                  <div
                    style={{
                      display: "flex",
                      gap: "5px",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      className="Update-delete-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(news);
                      }}
                      width="30"
                      height="30"
                      src="https://img.icons8.com/ios-glyphs/30/create-new.png"
                      alt="create-new"
                    />

                    <img
                      className="Update-delete-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(news._id);
                      }}
                      width="30"
                      height="30"
                      src="https://img.icons8.com/ios-glyphs/30/filled-trash.png"
                      alt="filled-trash"
                    />
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="12" className="text-center">
                No news available.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      <div className="d-flex justify-content-center mt-3">
        {paginationRange.map((page) => (
          <Button
            key={page}
            variant={currentPage === page ? "primary" : "outline-primary"}
            onClick={() => setCurrentPage(page)}
            className="mx-1"
          >
            {page}
          </Button>
        ))}
        {totalPages > 10 && currentPage + 5 < totalPages && (
          <>
            <Button
              variant="outline-primary"
              onClick={() => setCurrentPage(currentPage + 1)}
              className="mx-1"
            >
              Next
            </Button>
          </>
        )}
      </div>
      <CustomModal isOpen={isModalOpen} onClose={closeModal}>
        <div style={{ padding: "1rem", maxHeight: "80vh", overflowY: "auto" }}>
          <h4
            style={{
              fontSize: "1.2rem",
              fontWeight: "bold",
              textAlign: "center",
              marginBottom: "20px",
            }}
          >
            {isEditing ? "Edit News" : "Create News"}
          </h4>
          <CustomForm onSubmit={handleSubmit}>
            <Form.Group>
              <Form.Label>Title</Form.Label>
              <Form.Control ref={titleRef} required />
            </Form.Group>
            <Form.Group>
              <Form.Label>Select News Type</Form.Label>
              <Form.Select
                placeholder="News type"
                name="News Type"
                value={newsType}
                onChange={(e) => setNewsType(e.target.value)}
              >
                <option value="">Select News Type</option>
                <option value="NIL">NIL</option>
                <option value="TransferPortal">TransferPortal</option>
                <option value="Recruit">Recruit</option>
                <option value="General">General</option>
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>Select Sports</Form.Label>
              <div className="custom-dropdown">
                <button
                  type="button"
                  onClick={toggleDropdown}
                  style={{
                    width: "100%",
                    padding: "10px",
                    textAlign: "left",
                    border: "none",
                    background: "transparent",
                  }}
                >
                  {selectedSports.length > 0
                    ? selectedSports.join(", ")
                    : "Select Sports"}
                </button>
                {dropdownOpen && (
                  <div>
                    {favSports.map((sport) => (
                      <div
                        key={sport._id}
                        onClick={() => handleSelect(sport.name)}
                        style={{ padding: "10px", cursor: "pointer" }}
                      >
                        {sport.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Form.Group>

            <Form.Group>
              <Form.Label>Category</Form.Label>
              <Form.Control ref={categoryRef} required />
            </Form.Group>
            <Form.Group>
              <Form.Label>Source</Form.Label>
              <Form.Control ref={sourceRef} required />
            </Form.Group>

            <Form.Group className="pb-4" controlId="tags">
              <Form.Label>Tags</Form.Label>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
              >
                {tags.map((tag, index) => (
                  <span
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      margin: "2px",
                      backgroundColor: "#e0e0e0",
                      borderRadius: "3px",
                    }}
                  >
                    {tag}
                    <button
                      onClick={() => removeTag(tag)}
                      style={{
                        marginLeft: "5px",
                        border: "none",
                        background: "none",
                        cursor: "pointer",
                        color: "#ce1126",
                      }}
                    >
                      x
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  placeholder="Enter tags"
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  style={{
                    border: "none",
                    outline: "none",
                    flex: "1",
                    minWidth: "100px",
                    padding: "6px 18px",
                  }}
                />
              </div>
            </Form.Group>
            <Form.Group>
              <Form.Label>Details</Form.Label>
              <Form.Control as="textarea" ref={detailsRef} required />
            </Form.Group>
            <Form.Group>
              <Form.Label>URL</Form.Label>
              <Form.Control ref={urlRef} required />
            </Form.Group>
            <Form.Group>
              <Form.Label>School Name</Form.Label>
              <Form.Select ref={schoolNameRef} required>
                <option value="">Select School</option>
                {schools.map((school) => (
                  <option key={school._id} value={school.name}>
                    {school.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>School Mascot</Form.Label>
              <Form.Control ref={schoolMascotRef} required />
            </Form.Group>
            <Form.Group>
              <Form.Label>Gender</Form.Label>
              <Form.Select ref={genderRef} required>
                <option value="">Select Gender</option>
                {genderOptions.map((gender) => (
                  <option key={gender} value={gender}>
                    {gender}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>Image</Form.Label>
              <Form.Control type="file" onChange={handleImageChange} />
              {image && (
                <Image src={image} alt="Preview" width="100" className="mt-2" />
              )}
            </Form.Group>
            <div className="d-flex justify-content-end mt-3">
              <Button variant="secondary" onClick={closeModal} className="me-2">
                Cancel
              </Button>
              <CustomBtn loading={saveButton} variant="primary" type="submit">
                {isEditing ? "Update" : "Save"}
              </CustomBtn>
            </div>
          </CustomForm>
        </div>
      </CustomModal>

      <CustomModal isOpen={isViewModalOpen} onClose={closeViewModal}>
        <div
          style={{
            maxWidth: "600px",
            padding: "20px",
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <h3>News Details</h3>
          <p>
            <strong>Title:</strong> {viewedNews?.title}
          </p>
          <p>
            <strong>Category:</strong> {viewedNews?.category}
          </p>
          <p>
            <strong>News Type:</strong> {viewedNews?.newsType}
          </p>
          <p>
            <strong>Source:</strong> {viewedNews?.source}
          </p>
          <p>
            <strong>School Name:</strong> {viewedNews?.schoolName}
          </p>
          <p>
            <strong>School Mascot:</strong> {viewedNews?.schoolMascot}
          </p>
          <p>
            <strong>Gender:</strong> {viewedNews?.gender}
          </p>
          <p>
            <strong>URL:</strong> {viewedNews?.url}
          </p>
          <p>
            <strong>Sports Tags:</strong>
            {viewedNews?.sports_tags && viewedNews?.sports_tags.length > 0
              ? (() => {
                  try {
                    const parsedSport = JSON.parse(viewedNews?.sports_tags[0]);
                    return parsedSport.join(",");
                  } catch (error) {
                    console.error("Error parsing tags:", error);
                    return viewedNews?.sports_tags[0];
                  }
                })()
              : "No Sports available"}
          </p>
          <p>
            <strong>Tags:</strong>{" "}
            {viewedNews?.tags && viewedNews.tags.length > 0
              ? (() => {
                  try {
                    const parsedTags = JSON.parse(viewedNews.tags[0]);
                    return parsedTags.join(",");
                  } catch (error) {
                    console.error("Error parsing tags:", error);
                    return viewedNews.tags[0];
                  }
                })()
              : "No tags available"}
          </p>
          <p>
            <strong>Details:</strong> {viewedNews?.details}
          </p>

          {viewedNews?.image && (
            <div className="mt-2">
              <p>
                <strong>Image:</strong>
              </p>
              <Image
                src={`${baseUrl}/${viewedNews.image}`}
                alt="news"
                thumbnail
                width="200"
              />
            </div>
          )}
        </div>
      </CustomModal>
    </div>
  );
};

export default NewsManagement;
